import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { hslToHex, hexToHsl, formatColorLabel } from "@/lib/theme/utils";
import type { ThemeColors } from "@/lib/theme/types";

interface ColorPickerProps {
  colorKey: keyof ThemeColors;
  value: string;
  onChange: (key: keyof ThemeColors, value: string) => void;
}

/**
 * HSL color picker with visual preview and hex color input
 */
export function ColorPicker({ colorKey, value, onChange }: ColorPickerProps) {
  const hexValue = hslToHex(value);
  const label = formatColorLabel(colorKey);

  const handleHexChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const hex = e.target.value;
    const hsl = hexToHsl(hex);
    onChange(colorKey, hsl);
  };

  const handleHslChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(colorKey, e.target.value);
  };

  return (
    <div className="flex items-center gap-3 py-2">
      {/* Color preview and picker */}
      <div className="relative">
        <div
          className="h-8 w-8 rounded-md border border-border shadow-sm"
          style={{ backgroundColor: `hsl(${value})` }}
        />
        <input
          type="color"
          value={hexValue}
          onChange={handleHexChange}
          className="absolute inset-0 h-8 w-8 cursor-pointer opacity-0"
          title={`Pick ${label} color`}
          autoComplete="off"
          data-1p-ignore
          data-lpignore="true"
          data-form-type="other"
        />
      </div>

      {/* Label */}
      <Label className="min-w-[120px] text-sm font-medium">{label}</Label>

      {/* HSL value input */}
      <Input
        type="text"
        value={value}
        onChange={handleHslChange}
        className="h-8 flex-1 font-mono text-xs"
        placeholder="H S% L%"
        title="HSL value (e.g., 262 83% 58%)"
        autoComplete="off"
        data-1p-ignore
        data-lpignore="true"
        data-form-type="other"
      />
    </div>
  );
}
